import type { LanguageType } from '@/types/language';

import type { FaqEntry } from './faq';

export const FAQ_AI_COPY = {
  ko: {
    answerAction: '이 답변을 AI에게 더 물어보기',
    searchAction: '이 질문을 AI에게 물어보기',
    choose: '함께 살펴볼 AI를 선택하세요.',
    newWindow: '새 창에서 열림',
    copy: '질문 복사',
    copied: '질문을 복사했어요. AI 대화에 붙여넣어 주세요.',
    failed: '자동으로 복사하지 못했어요. 아래 내용을 선택해 복사해 주세요.',
    promptLabel: 'AI에게 물어볼 내용',
    help: '질문이 입력되지 않으면 복사해서 붙여넣어 주세요.',
  },
  en: {
    answerAction: 'Explore this answer with AI',
    searchAction: 'Ask AI about this question',
    choose: 'Choose an AI to explore this with.',
    newWindow: 'Opens in a new window',
    copy: 'Copy question',
    copied: 'Question copied. Paste it into your AI conversation.',
    failed: 'Could not copy automatically. Select and copy the text below.',
    promptLabel: 'Question for AI',
    help: 'If the question is not filled in, copy and paste it.',
  },
} as const;

export function buildFaqAiPrompt({
  locale,
  entry,
  question,
  groupId,
}: {
  locale: LanguageType;
  entry?: FaqEntry;
  question?: string;
  groupId?: string;
}) {
  const source = `https://deeplyinc.com${locale === 'ko' ? '/ko' : ''}/faq${groupId ? `#faq-${groupId}` : ''}`;
  if (locale === 'ko') {
    return [
      '디플리(Deeply)의 공식 FAQ를 바탕으로 아래 질문을 함께 살펴봐 주세요. 한국어로 답변해 주세요.',
      `질문: ${entry?.question ?? question?.trim() ?? ''}`,
      entry
        ? `디플리 공식 답변: ${entry.answer}`
        : '아래 출처의 공식 FAQ를 먼저 확인해 주세요.',
      '공식 FAQ로 확인되는 내용과 추가 확인이 필요한 내용을 구분해 주세요. 답변을 쉽게 설명하고, 제 상황에 적용하기 전에 확인할 질문을 정리해 주세요. 제 상황이 필요한 경우 먼저 물어봐 주세요.',
      '공식 FAQ에 없는 성능, 기능, 견적, 도입 일정을 추측하거나 확정하지 마세요. 확인되지 않은 사항은 디플리에 문의하도록 안내하고 출처 URL을 함께 남겨 주세요.',
      `Source: ${source}`,
    ].join('\n\n');
  }
  return [
    'Help me explore the following question using Deeply’s official FAQ. Answer in English.',
    `Question: ${entry?.question ?? question?.trim() ?? ''}`,
    entry
      ? `Official Deeply answer: ${entry.answer}`
      : 'Read the official FAQ at the source below first.',
    'Distinguish what the official FAQ confirms from what needs further confirmation. Explain the answer simply and suggest questions to check before applying it to my situation. Ask about my situation when needed.',
    'Do not invent or promise performance, features, pricing, or deployment timelines absent from the official FAQ. Direct unconfirmed questions to Deeply and cite the source URL.',
    `Source: ${source}`,
  ].join('\n\n');
}
